import { supabase } from './supabaseClient';

const REFLECTIONS_TABLE = 'ml_learning_reflections_staging';

/**
 * Create a new reflection entry
 * @param {string} userId - User ID
 * @param {Date|string} date - Reflection date (YYYY-MM-DD format)
 * @param {Object} userResponses - User's answers to reflection questions
 * @param {string} reflectionEntry - The reflection text (manual for now)
 * @param {Object} metrics - Optional metrics object
 * @returns {Promise<Object>} - { data, error }
 */
export const createReflection = async (
  userId,
  date,
  userResponses,
  reflectionEntry,
  metrics = {}
) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .insert({
      user_id: userId,
      date: date,
      user_responses: userResponses,
      reflection_entry: reflectionEntry,
      metrics: metrics,
    })
    .select()
    .single();

  return { data, error };
};

/**
 * Update an existing reflection entry
 * @param {string} userId - User ID
 * @param {Date|string} date - Reflection date
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - { data, error }
 */
export const updateReflection = async (userId, date, updates) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .update(updates)
    .eq('user_id', userId)
    .eq('date', date)
    .select()
    .single();

  return { data, error };
};

/**
 * Fetch a specific reflection entry by date
 * @param {string} userId - User ID
 * @param {Date|string} date - Reflection date (YYYY-MM-DD)
 * @returns {Promise<Object>} - { data, error }
 */
export const fetchReflectionByDate = async (userId, date) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('date', date)
    .maybeSingle();

  return { data, error };
};

/**
 * Fetch all reflections for a user
 * @param {string} userId - User ID
 * @param {number} limit - Number of entries to fetch
 * @param {number} offset - Pagination offset
 * @returns {Promise<Object>} - { data: entries[], error }
 */
export const fetchReflections = async (userId, limit = 30, offset = 0) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .range(offset, offset + limit - 1);

  return { data, error };
};

/**
 * Fetch reflections for a specific date range
 * @param {string} userId - User ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} - { data: entries[], error }
 */
export const fetchReflectionsByDateRange = async (userId, startDate, endDate) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false });

  return { data, error };
};

/**
 * Delete a reflection entry
 * @param {string} userId - User ID
 * @param {Date|string} date - Reflection date
 * @returns {Promise<Object>} - { data, error }
 */
export const deleteReflection = async (userId, date) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .delete()
    .eq('user_id', userId)
    .eq('date', date)
    .select()
    .single();

  return { data, error };
};

/**
 * Check if a reflection exists for a specific date
 * @param {string} userId - User ID
 * @param {Date|string} date - Reflection date
 * @returns {Promise<boolean>} - true if exists, false otherwise
 */
export const reflectionExists = async (userId, date) => {
  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .select('id')
    .eq('user_id', userId)
    .eq('date', date)
    .maybeSingle();

  return !error && data !== null;
};

/**
 * Get dates that have reflections for a given month
 * @param {string} userId - User ID
 * @param {number} year - Year (e.g., 2024)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} - { data: dates[], error }
 */
export const getReflectionDatesForMonth = async (userId, year, month) => {
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const endDate = new Date(year, month, 0).toISOString().split('T')[0]; // Last day of month

  const { data, error } = await supabase
    .from(REFLECTIONS_TABLE)
    .select('date')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true });

  if (error) return { data: null, error };

  // Return array of date strings
  return { data: data.map((item) => item.date), error: null };
};
